import * as React from 'react';
import moment from 'moment';
import Axios from 'axios';
import { formatPhoneNumber } from 'utils';
import { ListingLabelContext, parseLabelFromString } from 'contexts';
import { ListingAvailability, ListingStatus } from '@bb/domain/dist/types';
import { useEffect, useState } from 'react';
// @ts-ignore angular import
import { EyeballButton } from '../preview/preview_button.component';
import AgentChatLink from '../../react-common/AgentChatLink';

interface State {
  listing?: any;
  imageUrls: Image[];
}

interface Image {
  url: string;
}

const Listing: React.FC<any> = (props: any) => {
  let previewContainer: HTMLElement | null;
  let previewScope: any;

  const [state, setState] = useState<State>({
    imageUrls: [],
  });

  const { getLabel, getAvailability } = React.useContext(ListingLabelContext);

  const carousel = (id: string, action: any) => () => {
    (window as any).$(`#${id}`).carousel(action);
  };

  useEffect(() => {
    const fetchData = async () => {
      const {
        listing: { id: listingId },
      } = props;

      const response = await Axios.get(`/listing/${listingId}`);

      const listing = response.data;
      if (
        listing.reps &&
        listing.reps[0] &&
        listing.reps[0].location &&
        listing.reps[0].address
      ) {
        listing.address =
          listing.org.addresses[listing.reps[0].location][
            listing.reps[0].address
          ];
      }

      setState(prevState => ({ ...prevState, listing }));

      const { $rootScope, $compile } = props;
      previewScope = $rootScope.$new(false);
      previewScope.listing = props.listing;
      previewScope.$apply(() => {
        $compile(previewContainer)(previewScope);
      });
      carousel(props.listing.id, { interval: 3000000 })();

      // File Search
      const { data } = await Axios.get(`/file/${listingId}/images`);
      const imageUrls: Image[] = [];
      data.forEach((image: any) => {
        imageUrls.push(image);
      });
      setState(prevState => ({
        ...prevState,
        imageUrls: prevState.imageUrls.concat(imageUrls),
      }));
    };

    fetchData();

    const componentWillUnmount = () => {
      if (previewScope) previewScope.$destroy();
    };

    return componentWillUnmount;
  }, []);

  const { listing, imageUrls } = state;
  const { $rootScope } = props;

  if (!listing) {
    return (
      <div className="row">
        <div className="col-md-2 col-md-offset-5 spin">
          <i className="fa fa-spinner fa-spin" />
        </div>
      </div>
    );
  }

  let address;
  if (
    listing.reps &&
    listing.reps[0] &&
    listing.reps[0].location &&
    listing.reps[0].address
  ) {
    address =
      listing.org.addresses[listing.reps[0].location][listing.reps[0].address];
  }

  const AvailabilityLabel = ({
    availability,
  }: {
    availability: ListingAvailability;
  }) => (
      <div>
        <label>{getLabel('Availability')}:</label>{' '}
        {getAvailability(availability)}
      </div>
    );

  const StatusLabel = ({ status }: { status: ListingStatus }) => (
      <div>
        <label>{getLabel('Status')}:</label> {getLabel(status)}
      </div>
    );

  return (
    <div>
      <div
        ref={c => {
          previewContainer = c;
        }}
      >
        <h1>
          <a href={`#/listing/${listing.id}/view`} className="listing-link">
            {props.listing.line1} {props.listing.line2}
          </a>
          <button className="btn btn-default pull-right" onClick={props.cancel}>
            <i className="fa fa-times" />
          </button>
          <EyeballButton listing={listing} />
        </h1>
      </div>
      <hr />
      <div className="row">
        <div className="col-md-5">
          <AvailabilityLabel availability={listing.availability} />
          <StatusLabel status={listing.status} />
          {listing.mls_numbers_formatted !== undefined && (
            <div>
              <label>MLS Number:</label> {listing.mls_numbers_formatted}
            </div>
          )}
          <div>
            <label>Expiry:</label>{' '}
            {moment(listing.expiry).format('MMM D, YYYY')}
          </div>
          {listing.dom !== undefined && (
            <div>
              <label>DOM:</label> {listing.dom}
            </div>
          )}
          <a href="">View History</a>
          <hr />
          <div>
            <label>List Price:</label> $
            {listing.type === 'commercial'
              ? listing.list_price.formatMoney(2)
              : listing.list_price.formatMoney(0)}
          </div>
          {listing.sold_details !== null &&
            listing.availability === 'Sold' &&
            listing.sold_details !== undefined && (
              <div>
                <div>
                  <label>Sold Price:</label>$
                  {listing.type === 'commercial'
                    ? listing.sold_details?.price?.formatMoney(2)
                    : listing.sold_details?.price?.formatMoney(0)}
                </div>
                <div>
                  <label>Sold Date:</label>{' '}
                  {moment(listing.sold_details.sold_date).format('MMM D, YYYY')}
                </div>
                <div>
                  <label>Closing Date:</label>{' '}
                  {moment(listing.sold_details.closed_date).format(
                    'MMM D, YYYY',
                  )}
                </div>
              </div>
            )}
          <hr />
          <div>
            <label>Type: </label>
            {
              // eslint-disable-next-line no-nested-ternary
              listing.type === 'commercial'
                ? 'Commercial'
                : listing.type === 'condo'
                  ? listing.condo && listing.condo.type
                  : listing.exteriorType
            }
          </div>
          {listing.type !== 'commercial' && (
            <div>
              <label>Style: </label>
              {listing.type === 'condo'
                ? listing.condo && listing.condo.style
                : listing.style}
            </div>
          )}
        </div>
        <div className="col-md-7">
          {imageUrls.length > 0 && (
            <div style={{ position: 'relative' }}>
              <div id={listing.id} data-ride="carousel" data-interval="3000000">
                <div className="carousel-inner" role="listbox">
                  {imageUrls.map(({ url }, index) => (
                    <div
                      className={`item${index === 0 ? ' active' : ''}`}
                      key={index}
                    >
                      <div
                        style={{
                          background: `url(${
                            url
                              ? `https://process.filestackapi.com/AVsLFCmooSeCfsjDRz3Cbz/resize=fit:crop,h:300,w:500/${url}`
                              : '/img/noimage.jpg'
                          }) no-repeat center center`,
                          backgroundSize: 'cover',
                          display: 'block',
                          width: '100%',
                          height: '150px',
                        }}
                      />
                      <br />
                      <div
                        className="carousel-caption hidden-xs"
                        style={{ paddingBottom: 10 }}
                      >
                        <a
                          data-toggle="modal"
                          data-target={`#${listing.id}photoModal`}
                        >
                          <i
                            className="fa fa-arrows-alt"
                            style={{
                              fontSize: 30,
                              color: 'white',
                              marginLeft: 20,
                            }}
                          />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                {imageUrls.length > 1 && (
                  <a
                    className="left carousel-control"
                    role="button"
                    onClick={carousel(listing.id, 'prev')}
                  >
                    <span
                      className="glyphicon glyphicon-chevron-left"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Previous</span>
                  </a>
                )}
                {imageUrls.length > 1 && (
                  <a
                    className="right carousel-control"
                    role="button"
                    onClick={carousel(listing.id, 'next')}
                  >
                    <span
                      className="glyphicon glyphicon-chevron-right"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Next</span>
                  </a>
                )}
              </div>
            </div>
          )}
          {listing.reps.map((rep: any, index: number) => (
            <div key={index} className="agent">
              <AgentChatLink
                agentId={rep.id}
                firstName={rep.firstName}
                lastName={rep.lastName}
                openContactDrawer={$rootScope.openContactDrawer}
              />
              <br />
              {!rep.pending && (
                <span>
                  {((rep.frontEndInstructions &&
                    rep.frontEndInstructions.releaseCell) ||
                    $rootScope.auth.isAdmin) && (
                    <span>
                      {formatPhoneNumber(rep.phone)}
                      <br />
                    </span>
                  )}
                  {rep.email}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-md-7">
          {(address && address.name) || listing.org.mls_name}
          <br />
          {address && address.address}
        </div>
        {address && (
          <div className="col-md-5">
            <label>Phone</label> {formatPhoneNumber(address.phone)}
            <br />
            <label>Fax</label> {formatPhoneNumber(address.fax)}
          </div>
        )}
      </div>
      <div
        className="modal"
        id={`${listing.id}photoModal`}
        tabIndex={-1}
        role="dialog"
        aria-hidden="true"
      >
        <div className="modal-dialog" style={{ width: '80%', marginTop: 20 }}>
          <div className="modal-content">
            <div className="modal-body">
              <div style={{ position: 'relative' }}>
                <div
                  id={`${listing.id}modal`}
                  data-ride="carousel"
                  data-interval="3000000"
                >
                  <div className="carousel-inner">
                    {imageUrls.map((imageUrl, index) => (
                      <div
                        key={index}
                        className={`item${index === 0 ? ' active' : ''}`}
                      >
                        <img style={{ margin: 'auto' }} src={imageUrl.url} />
                      </div>
                    ))}
                  </div>
                  {imageUrls.length > 1 && (
                    <a
                      className="left carousel-control"
                      role="button"
                      onClick={carousel(`${listing.id}modal`, 'prev')}
                    >
                      <span
                        className="glyphicon glyphicon-chevron-left"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Previous</span>
                    </a>
                  )}
                  {imageUrls.length > 1 && (
                    <a
                      className="right carousel-control"
                      role="button"
                      onClick={carousel(`${listing.id}modal`, 'next')}
                    >
                      <span
                        className="glyphicon glyphicon-chevron-right"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Next</span>
                    </a>
                  )}
                </div>
              </div>
              <div className="modal-footer" style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className="btn btn-default"
                  data-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Listing;
